import moment from 'moment';
import { SCHEDULE_FIELDS_TO_COMPARE, SCHEDULE_REQUIRED_FIELDS, CODE_TYPE_COLORS } from '../constants';
import { buildCodeKey, buildCodeKeyFromCode } from '~/utils/codes';

export const buildInitialScheduleForm = (equipment = {}) => {
  const now = moment();

  return {
    equipaments_id: equipment.equip_type,
    equipament_groups_id: equipment.id_equip_group,
    group_equipament_links_id: equipment.id_equip,
    codes_id: 0,
    code_groups_id: 0,
    code_types_id: -1,
    flowchart_element_id: 0,
    flowchart_element_point_id: 0,
    start_date: now.format('DD/MM/YYYY'),
    start_time: now.format('HH:mm:ss'),
    end_time: null,
    end_date: null,
    delay: null,
    duration: '00:00:00',
    observation: '',
  };
};

const sortOptionsByLabel = options => [...options].sort((a, b) => {
  const labelA = String(a?.label ?? '').trim();
  const labelB = String(b?.label ?? '').trim();

  return labelA.localeCompare(labelB, undefined, {
    sensitivity: 'base',
    numeric: true,
    ignorePunctuation: true,
  });
});


export const buildElementsOptions = (lookupById) => {
  const elementsOptions = Object.values(lookupById ?? {})
    .map(element => ({
      label: element.name,
      value: element.id,
    }));
  return sortOptionsByLabel(elementsOptions);
};

export const buildSubElementsOptions = (lookupById, equip, key = 'element_id') => {
  const value = equip?.[key] ?? 0;
  const subElementsOptions = Object.values(lookupById ?? {})
    .filter(subElement => subElement.id_element === value)
    .map(subElement => ({
      label: subElement.name,
      value: subElement.id,
    }));
  return sortOptionsByLabel(subElementsOptions);
};


export const buildCodesOptions = (codes, form) => {
  const { equipaments_id } = form ?? {};
  const codesOptions = (codes ?? [])
    .filter(code => code.id_equip === equipaments_id && code.id)
    .filter(code => (code.is_visible === 1 && code.id !== -1))
    .map(code => ({
      label: code.name,
      value: buildCodeKeyFromCode(code),
      dotColor: CODE_TYPE_COLORS[code.code_type] ?? '#666666',
    }));
  return sortOptionsByLabel(codesOptions);
};

export const getSelectedCodeOptionValue = (codesByKey, form) => {
  const selectedCode = codesByKey?.[buildCodeKey({
    id: form?.codes_id,
    equipType: form?.equipaments_id,
    codeGroup: form?.code_groups_id,
    codeType: form?.code_types_id,
  })];
  return selectedCode ? buildCodeKeyFromCode(selectedCode) : 0;
};

const isFieldValueEmpty = (key, value, codeTypeKey = 'code_type') => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim().length === 0) return true;

  if (key === codeTypeKey) {
    return typeof value === 'number' && value < 0;
  }

  return typeof value === 'number' && value === 0;
};

export const formatDateTime = (date) => {
  if (!date) return null;
  const momentDate = moment(date);

  return {
    start_date: momentDate.format('DD/MM/YYYY'),
    start_time: momentDate.format('HH:mm:ss'),
  };
};

export const getDateErrorMessage = (startDate, startTime, translate) => {
  const startDateTimeString = `${startDate} ${startTime}`;
  const startMoment = moment(startDateTimeString, 'DD/MM/YYYY HH:mm:ss', true);
  const now = moment();

  if (!startMoment.isValid()) {
    return translate('validation:InvalidDate');
  }
  if (!startMoment.isAfter(now)) {
    return translate('validation:DateMustBeInTheFuture');
  }
  return null;
};

export const calculateEndDateTime = (startDateStr, startTimeStr, durationStr) => {
  if (!startDateStr || !startTimeStr || !durationStr) {
    return {};
  }

  const startDateTimeStr = `${startDateStr} ${startTimeStr}`;
  const startMoment = moment(startDateTimeStr, 'DD/MM/YYYY HH:mm:ss');

  if (!startMoment.isValid()) {
    return {};
  }

  const durationParts = durationStr?.split(':') || [];
  const hours = Number(durationParts[0] || 0);
  const minutes = Number(durationParts[1] || 0);
  const seconds = Number(durationParts[2] || 0);

  const endMoment = startMoment.clone().add({
    hours,
    minutes,
    seconds,
  });

  return {
    end_date: endMoment.format('DD/MM/YYYY'),
    end_time: endMoment.format('HH:mm:ss'),
  };
};

export const applyFieldCascade = (form, field, value, lookupCtx) => {
  const { codesByKey } = lookupCtx;
  const next = { ...form, [field]: value };

  if (field === 'codes_id') {
    // `value` é a chave única do código (ver `buildCodeKey`), pois o id sozinho não o identifica.
    const selectedCode = codesByKey?.[value];
    next.code_types_id = selectedCode?.code_type ?? -1;
    next.code_groups_id = selectedCode?.id_group ?? 0;
    next.codes_id = selectedCode?.id ?? 0;
  }

  if (field === 'flowchart_element_id') {
    next.flowchart_element_point_id = 0;
  }

  if (field === 'start_date') {
    const { start_date, start_time } = formatDateTime(value) || {};
    next.start_date = start_date;
    next.start_time = start_time;

    const currentDuration = form.duration;
    if (currentDuration && currentDuration !== '00:00:00') {
      const endDateTime = calculateEndDateTime(start_date, start_time, currentDuration);
      next.end_date = endDateTime.end_date;
      next.end_time = endDateTime.end_time;
    }
  }

  if (field === 'duration') {
    if (!value || value === '00:00:00') {
      next.duration = null;
      next.end_date = null;
      next.end_time = null;
    } else {
      const endDateTime = calculateEndDateTime(form.start_date, form.start_time, value);
      next.end_date = endDateTime.end_date;
      next.end_time = endDateTime.end_time;
    }
  }

  return next;
};

const isDurationValid = (value) => {
  if (!value || value === '00:00:00' || value === '') return false;
  return Boolean(value.match(/\d{2}:\d{2}:\d{2}/));
};

export const isFormValid = (dateError, form) => {
  if (dateError) return false;

  return SCHEDULE_REQUIRED_FIELDS.every((key) => {
    const value = form[key];

    if (key === 'duration') {
      return isDurationValid(value);
    }
    if (isFieldValueEmpty(key, value, 'code_types_id')) {
      return false;
    }
    return true;
  });
};

export const hasFormChanged = (form, originalForm) => SCHEDULE_FIELDS_TO_COMPARE.some((key) => {
  const currentValue = form[key] ?? '';
  const originalValue = originalForm[key] ?? '';

  return currentValue !== originalValue;
});
